import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import {
  getProjectRACI,
  createRaci,
  updateRaci,
  archiveRaci,
  getPlantRaci,
  getRaciById,
  getVersions as getVersionsApi,
  addVersion as addVersionApi,
  getAllDocTransmittalsForPlant,
  resolvePlantIdForDocTransmittal,
} from "./api";
import {
  buildDocTransmittalNumberLookup,
  enrichRacisWithTransmittalNumbers,
} from "./docTransmittalRaciUtils";
import {
  getTimesheets,
  createTimesheet,
  updateTimesheet,
  deleteTimesheet,
} from "./timesheetApi";
import { getComments, createComment } from "./commentsApi";
import { secondAxios } from "../../../../../../api/axios";
import { useParams } from "react-router-dom";
import {
  getPlantDocumentList,
  getProjectDocumentList,
} from "./Dashboard/docListAPI";
import { useAuthContext } from "../../../../../../context/authContext";
import { authStore } from "../../../../../../context/authContext/authStore";
import { getAllTasks } from "../../../../Project/plant/PlantTaskPage/TaskList/api";
import { createTimesheet as createTaskTimesheet } from "../../../../Project/plant/PlantTaskPage/TaskList/TimesheetsDialog/api";
import { parseTaskCode } from "../../../../Project/plant/PlantTaskPage/TaskList/components/taskCodeUtils";

export const DCSContext = createContext(null);

export const useDCS = () => useContext(DCSContext);

const isRaciArchived = (raci) =>
  raci?.archived === true ||
  raci?.isArchived === true ||
  raci?.status === "archived";

export const DCSProvider = ({ children, type, plant }) => {
  const { id, plantId, projectId } = useParams();
  const activeProjectId = projectId || id;

  const { user } = useAuthContext();

  const [racis, setRacis] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [documentList, setDocList] = useState([]);
  const [documentListLoaded, setDocumentListLoaded] = useState(false);
  const [currentDocsReviewer, setCurrentDocsReviewer] = useState("");
  const [plantProjects, setPlantProjects] = useState([]);
  const [selectedDocuments, setSelectedDocuments] = useState([]);
  const [documentTypeScope, setDocumentTypeScopeState] = useState(null);

  const effectiveType = documentTypeScope ?? type ?? "all";

  const setDocumentTypeScope = useCallback((scopeType) => {
    setDocumentTypeScopeState(scopeType);
  }, []);

  const documentFetchRef = useRef({ key: "", promise: null });

  const fetchDocumentList = useCallback(
    async (docType = "all") => {
      const normalizedType = docType || "all";
      const key = `${normalizedType}|${plant}|${plantId ?? ""}|${activeProjectId ?? ""}`;

      if (
        documentFetchRef.current.key === key &&
        documentFetchRef.current.promise
      ) {
        return documentFetchRef.current.promise;
      }

      const promise = (async () => {
        try {
          if (plant && plantId) {
            const list = await getPlantDocumentList(normalizedType, plantId);
            setDocList(list);
            return list;
          }
          if (activeProjectId) {
            const list = await getProjectDocumentList(
              normalizedType,
              activeProjectId,
            );
            setDocList(list);
            return list;
          }
          setDocList([]);
          return [];
        } finally {
          setDocumentListLoaded(true);
          if (documentFetchRef.current.key === key) {
            documentFetchRef.current = { key: "", promise: null };
          }
        }
      })();

      documentFetchRef.current = { key, promise };
      return promise;
    },
    [plant, plantId, activeProjectId],
  );

  // Returns the list, it does not just trigger the fetch: a caller that awaits
  // this needs the rows in hand. Reading them off the context state instead
  // would come back empty on a first load - setDocList has not re-rendered by
  // the time this resolves, which left the Document Register showing an empty
  // table until something else made it ask again.
  const getDocList = useCallback(
    () => fetchDocumentList("all"),
    [fetchDocumentList],
  );

  const getAllList = getDocList;

  const refreshRaci = useCallback(async () => {
    let racisList = [];
    const raciType = effectiveType;

    if (activeProjectId) {
      const racisFromApi = await getProjectRACI(activeProjectId, raciType);
      racisList = racisFromApi;
    }
    if (plant && plantId) {
      const plantData = await getPlantRaci(plantId);
      let allRacis = plantData?.racis || [];
      if (raciType && raciType !== "all") {
        allRacis = allRacis.filter((raci) => raci.type === raciType);
      }
      racisList = allRacis;
      setPlantProjects(plantData?.projects || []);
    } else if (!plant) {
      setPlantProjects([]);
    }

    if (racisList.length > 0) {
      const enrichedRacis = await Promise.all(
        racisList.map(async (raci) => {
          if (raci.id) {
            try {
              const fullRaciData = await getRaciById(raci.id);
              return { ...raci, ...fullRaciData };
            } catch (error) {
              console.error(
                `Error fetching full RACI data for ${raci.id}:`,
                error,
              );
              return raci;
            }
          }
          return raci;
        }),
      );
      let activeRacis = enrichedRacis.filter((raci) => !isRaciArchived(raci));

      const transmittalPlantId = await resolvePlantIdForDocTransmittal({
        plant,
        plantId,
        activeProjectId,
      });
      if (transmittalPlantId) {
        const transmittals = await getAllDocTransmittalsForPlant(
          transmittalPlantId,
        );
        const lookup = buildDocTransmittalNumberLookup(transmittals);
        activeRacis = enrichRacisWithTransmittalNumbers(activeRacis, lookup);
      }

      setRacis(activeRacis);
    } else {
      setRacis([]);
    }
  }, [activeProjectId, effectiveType, plant, plantId]);

  useEffect(() => {
    let cancelled = false;

    const loadUsers = async () => {
      try {
        const users = (await secondAxios.get("/user")).data;
        if (!cancelled) setAllUsers(users);
      } catch (error) {
        console.error("Failed to load DCS users:", error);
      }
    };

    loadUsers();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    setDocumentListLoaded(false);
    fetchDocumentList("all");
  }, [fetchDocumentList]);

  useEffect(() => {
    refreshRaci();
  }, [refreshRaci]);

  const addRaci = async (newRaci) => {
    const targetProjectId = newRaci?.projectId || activeProjectId;
    if (!targetProjectId) {
      console.warn("No project id available for creating RACI");
      return null;
    }
    const created = await createRaci(targetProjectId, newRaci);
    refreshRaci();
    return created;
  };

  const updateRaciData = async (raciId, updatedRaci) => {
    await updateRaci(raciId, updatedRaci);
    refreshRaci();
  };

  const archiveRaciData = async (raciId) => {
    setRacis((prev) => (prev || []).filter((raci) => raci.id !== raciId));

    try {
      await archiveRaci(raciId);
    } catch (error) {
      console.error("Error archiving RACI:", error);
      refreshRaci();
      throw error;
    }
  };

  const updateRaciUsers = (raciId, type, users) => {
    if (!raciId || !type) return;

    const fieldMap = {
      acc: "accountable",
      cons: "consulted",
      inf: "informed",
    };

    const field = fieldMap[type];
    if (!field) return;

    setRacis((prev) =>
      (prev || []).map((raci) =>
        raci.id === raciId
          ? {
              ...raci,
              [field]: users,
            }
          : raci,
      ),
    );
  };

  const getVersions = async (raciId) => {
    return await getVersionsApi(raciId);
  };

  const addVersion = async (raciId, versionData) => {
    await addVersionApi(raciId, versionData);
    refreshRaci();
  };

  const getTimesheetsData = (id) => getTimesheets(id);
  const createTimesheetData = async (id, data) => {
    data.user = user.name;
    await createTimesheet(id, data);

    // Best-effort: mirror this hour entry onto the 4D Schedule task that
    // was auto-created for this RACI's document (NewRaciModal.jsx tags
    // that task's taskCode with "...::<docNum>"). A miss here (task
    // deleted, older RACI predating this feature, etc.) just means the
    // hours don't show up in 4D Schedule - the RACI timesheet entry above
    // has already succeeded either way.
    if (plant && plantId) {
      try {
        const raci = racis.find((r) => String(r.id) === String(id));
        const docNum = raci?.docNum;
        if (docNum) {
          const tasks = await getAllTasks({ plantId });
          const matchedTask = (tasks || []).find(
            (t) => parseTaskCode(t.taskCode).uid === docNum,
          );
          if (matchedTask) {
            await createTaskTimesheet({
              timesheet: {
                date: data.date,
                hours: data.hours,
                progress: data.progress,
                workDone: data.workDone,
              },
              taskId: matchedTask.id,
              accessToken: authStore.getToken(),
            });
          }
        }
      } catch (error) {
        console.error(
          "Failed to mirror RACI timesheet onto its 4D Schedule task:",
          error,
        );
      }
    }
  };

  const updateTimesheetData = (id, data) => updateTimesheet(id, data);
  const deleteTimesheetData = (id) => deleteTimesheet(id);

  const getCommentsData = (id) => getComments(id);
  const createCommentData = (data) => createComment(data);

  return (
    <DCSContext.Provider
      value={{
        racis,
        allUsers,
        documentList,
        documentListLoaded,
        addRaci,
        updateRaciData,
        archiveRaciData,
        getVersions,
        addVersion,
        getTimesheetsData,
        createTimesheetData,
        updateTimesheetData,
        deleteTimesheetData,
        getCommentsData,
        createCommentData,
        type: effectiveType,
        id,
        getDocList,
        getAllList,
        currentDocsReviewer,
        setCurrentDocsReviewer,
        plantProjects,
        plant,
        plantId,
        activeProjectId,
        selectedDocuments,
        setSelectedDocuments,
        updateRaciUsers,
        setDocumentTypeScope,
      }}
    >
      {children}
    </DCSContext.Provider>
  );
};
